import React, { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../utils/useAuth";

export default function GameDetails() {
  const { id } = useParams();
  const { effectiveRole } = useAuth();
  const [game, setGame] = useState(null);
  const [officials, setOfficials] = useState({});

  useEffect(() => {
    async function load() {
      const ref = doc(db, "games", id);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const gameData = snap.data();
        setGame(gameData);
        
        // Load official details (contact info) for all assigned officials
        const officialIds = [
          gameData.assignedRefereeId,
          gameData.assistantReferee1Id,
          gameData.assistantReferee2Id,
          gameData.assistant4Id,
          gameData.assistant5Id,
          gameData.timeKeeperId,
          gameData.playerMovementRecorderId,
          gameData.statisticianId,
          gameData.technicalZoneManagerId
        ].filter(Boolean);

        const officialData = {};
        for (const officialId of officialIds) {
          const userRef = doc(db, "users", officialId);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            officialData[officialId] = userSnap.data();
          }
        }
        setOfficials(officialData);
      }
    }
    load();
  }, [id]);

  if (!game) return <div className="p-6">Loading game...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">

      <h2 className="text-3xl font-bold">Game Details</h2>

      {/* GAME INFORMATION */}
      <div className="bg-white p-6 rounded shadow space-y-3">
        <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-3">Match Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">Team A</p>
            <p className="font-semibold">{game.teamA} {game.teamAGrade}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Team B</p>
            <p className="font-semibold">{game.teamB} {game.teamBGrade}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Date & Time</p>
            <p className="font-semibold">{game.date} at {game.time}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Venue</p>
            <p className="font-semibold">{game.venue} (Field {game.field})</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Age Group</p>
            <p className="font-semibold">{game.ageGroup}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Competition</p>
            <p className="font-semibold">{game.competition}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Status</p>
            <p className="font-semibold uppercase text-green-600">{game.status}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Requested By</p>
            <p className="font-semibold">{game.coachName}</p>
          </div>
        </div>
      </div>

      {/* ASSIGNED OFFICIALS */}
      <div className="bg-white p-6 rounded shadow space-y-4">
        <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-3">Assigned Match Officials</h3>
        
        {game.assignedRefereeId ? (
          <div className="space-y-3">
            {/* Main Referee */}
            {game.assignedRefereeId && (
              <div className="p-3 bg-green-50 border-l-4 border-green-600 rounded">
                <p className="text-sm text-gray-600">Main Referee</p>
                <p className="font-semibold text-lg">{game.assignedRefereeName}</p>
                {game.assignedRefereeLeague && (
                  <p className="text-sm text-gray-600">{game.assignedRefereeLeague}</p>
                )}
                {officials[game.assignedRefereeId] && (
                  <div className="mt-2 text-sm">
                    <p>📞 {officials[game.assignedRefereeId].phoneNumber || officials[game.assignedRefereeId].contactNumber || "N/A"}</p>
                    <p>📧 {officials[game.assignedRefereeId].email}</p>
                  </div>
                )}
                {game.refereeStatus && (
                  <p className="mt-2 text-xs">
                    Status: <span className={`font-semibold ${game.refereeStatus === 'accepted' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {game.refereeStatus.toUpperCase()}
                    </span>
                  </p>
                )}
              </div>
            )}

            {/* Assistant Referee 1 */}
            {game.assistantReferee1Id && (
              <div className="p-3 bg-blue-50 border-l-4 border-blue-600 rounded">
                <p className="text-sm text-gray-600">Assistant Referee 1</p>
                <p className="font-semibold">{game.assistantReferee1Name}</p>
                {game.assistantReferee1League && (
                  <p className="text-sm text-gray-600">{game.assistantReferee1League}</p>
                )}
                {officials[game.assistantReferee1Id] && (
                  <div className="mt-2 text-sm">
                    <p>📞 {officials[game.assistantReferee1Id].phoneNumber || officials[game.assistantReferee1Id].contactNumber || "N/A"}</p>
                  </div>
                )}
              </div>
            )}

            {/* Assistant Referee 2 */}
            {game.assistantReferee2Id && (
              <div className="p-3 bg-blue-50 border-l-4 border-blue-600 rounded">
                <p className="text-sm text-gray-600">Assistant Referee 2</p>
                <p className="font-semibold">{game.assistantReferee2Name}</p>
                {game.assistantReferee2League && (
                  <p className="text-sm text-gray-600">{game.assistantReferee2League}</p>
                )}
                {officials[game.assistantReferee2Id] && (
                  <div className="mt-2 text-sm">
                    <p>📞 {officials[game.assistantReferee2Id].phoneNumber || officials[game.assistantReferee2Id].contactNumber || "N/A"}</p>
                  </div>
                )}
              </div>
            )}

            {/* Other Officials */}
            {game.assistant4Id && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Assistant 4</p>
                <p className="font-semibold">{game.assistant4Name}</p>
              </div>
            )}

            {game.assistant5Id && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Assistant 5</p>
                <p className="font-semibold">{game.assistant5Name}</p>
              </div>
            )}

            {game.timeKeeperId && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Time Keeper</p>
                <p className="font-semibold">{game.timeKeeperName}</p>
              </div>
            )}

            {game.playerMovementRecorderId && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Player Movement Recorder</p>
                <p className="font-semibold">{game.playerMovementRecorderName}</p>
              </div>
            )}

            {game.statisticianId && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Statistician</p>
                <p className="font-semibold">{game.statisticianName}</p>
              </div>
            )}

            {game.technicalZoneManagerId && (
              <div className="p-3 bg-gray-50 border-l-4 border-gray-400 rounded">
                <p className="text-sm text-gray-600">Technical Zone Manager</p>
                <p className="font-semibold">{game.technicalZoneManagerName}</p>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500">No officials assigned yet</p>
        )}
      </div>

      {/* Final Score */}
      {game.finalScore && (
        <div className="bg-white p-6 rounded shadow">
          <h3 className="text-xl font-bold text-gray-800 border-b pb-2 mb-3">Final Score</h3>
          <p className="text-2xl font-bold text-center">{game.finalScore}</p>
        </div>
      )}

      <Link
        to={
          effectiveRole === "manager" ? "/manager" :
          effectiveRole === "referee" ? "/ref" :
          effectiveRole === "admin" ? "/admin/users" :
          "/coach"
        }
        className="inline-block px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
      >
        ← Back to Dashboard
      </Link>

    </div>
  );
}
